import { useEffect, useState, useCallback } from "react";
import { Barcode, Eye, PaperPlaneTilt, ArrowsClockwise, FilePdf, FileCode, XCircle, Trash, Plus, CircleNotch, NotePencil, WifiSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { downloadBlob, blobError } from "../../lib/download";
import { fmtBRL } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { validateNfeForm, isAutorizada, downloadCcePdf, latestCceSeq } from "./nfeActions";
import { NfeForm } from "./NfeForm";
import { NfeConfirmDialog, useOnline } from "./NfeConfirmDialog";
import { CceDialog } from "./CceDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const STATUS_STYLE = {
  autorizado: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  erro_cancelamento: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  processando_autorizacao: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  enviando: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  desconhecido: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  cancelado: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};
const STATUS_LABEL = {
  autorizado: "Autorizada", processando_autorizacao: "Processando", enviando: "Enviando",
  desconhecido: "Confirmar envio", cancelado: "Cancelada", erro_autorizacao: "Rejeitada",
  nao_encontrado: "Não recebida", erro_cancelamento: "Autorizada (cancelamento recusado)",
};

const Chip = ({ status }) => (
  <span className={`text-[11px] font-medium border rounded-full px-2.5 py-0.5 ${STATUS_STYLE[status] || "bg-rose-500/15 text-rose-400 border-rose-500/30"}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

export const NfeDialog = ({ order, onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [notes, setNotes] = useState(null);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState({ ambiente: "homologacao", settings_ok: false, emitente_ok: false });
  const [busy, setBusy] = useState(false);
  const [cceNote, setCceNote] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const online = useOnline();

  const load = useCallback(async () => {
    try {
      const [{ data: ns }, { data: pf }] = await Promise.all([
        api.get("/nfe/notes", { params: { order_id: order.id } }),
        api.get(`/nfe/prefill/${order.id}`),
      ]);
      setNotes(ns);
      setForm({
        ...pf.form,
        itens: (pf.form.itens || []).map((it) => ({ ...it, _k: Math.random().toString(36).slice(2) })),
        duplicatas: (pf.form.duplicatas || []).map((d) => ({ ...d, _k: Math.random().toString(36).slice(2) })),
      });
      setMeta({ ambiente: pf.ambiente, settings_ok: pf.settings_ok, emitente_ok: pf.emitente_ok, ultimo_numero: pf.ultimo_numero || 0 });
      const active = ns.some((n) => ["autorizado", "erro_cancelamento", "processando_autorizacao", "enviando", "desconhecido"].includes(n.status));
      setView(ns.length === 0 || !active ? (ns.length === 0 ? "form" : "list") : "list");
    } catch (err) { toast.error(apiError(err)); }
  }, [order.id]);

  useEffect(() => { load(); }, [load]);

  const preview = async () => {
    setBusy(true);
    try {
      const res = await api.post("/nfe/preview", { form, order_code: order.code }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank");
      if (!win) downloadBlob(res.data, `danfe_previa_${order.code}.pdf`);
      toast.success("Prévia do DANFE gerada — sem valor fiscal");
    } catch (err) { toast.error(await blobError(err, "Erro ao gerar a prévia")); }
    setBusy(false);
  };

  const emit = () => {
    const errs = validateNfeForm(form);
    if (errs.length) {
      errs.slice(0, 4).forEach((e) => toast.error(e));
      return;
    }
    setConfirmOpen(true);
  };

  const doEmit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/nfe/emit", { order_id: order.id, form });
      toast.success(data.status === "autorizado" ? `NF-e autorizada!` : "NF-e enviada — aguardando autorização da SEFAZ");
      setConfirmOpen(false);
      await load();
      setView("list");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const refresh = async (n) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/nfe/notes/${n.id}`);
      setNotes((ns) => ns.map((x) => (x.id === n.id ? { ...x, ...data } : x)));
      toast.success(`Status: ${STATUS_LABEL[data.status] || data.status}${data.mensagem_sefaz ? ` — ${data.mensagem_sefaz}` : ""}`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const danfe = async (n) => {
    setBusy(true);
    try {
      const res = await api.get(`/nfe/notes/${n.id}/danfe`, { responseType: "blob" });
      downloadBlob(res.data, `danfe_${order.code}_${n.numero || n.ref}.pdf`);
    } catch (err) { toast.error(await blobError(err, "Erro ao gerar o DANFE")); }
    setBusy(false);
  };

  const xml = async (n) => {
    setBusy(true);
    try {
      const res = await api.get(`/nfe/notes/${n.id}/xml`, { responseType: "blob" });
      downloadBlob(res.data, `nfe_${n.numero || n.ref}.xml`);
    } catch (err) { toast.error(await blobError(err, "Erro ao baixar o XML")); }
    setBusy(false);
  };

  const ccePdf = async (n) => {
    setBusy(true);
    try { await downloadCcePdf(n, latestCceSeq(n)); }
    catch (err) { toast.error(await blobError(err, "Erro ao baixar o PDF da CC-e"), { duration: 12000 }); }
    setBusy(false);
  };

  const cancel = async (n) => {
    const just = window.prompt("Justificativa do cancelamento (mínimo 15 caracteres):", "Cancelamento por erro de emissão no pedido");
    if (just == null) return;
    setBusy(true);
    try {
      await api.post(`/nfe/notes/${n.id}/cancel`, { justificativa: just });
      toast.success("NF-e cancelada");
    } catch (err) { toast.error(apiError(err), { duration: 15000 }); }
    await load();
    setBusy(false);
  };

  const removeNote = async (n) => {
    if (!window.confirm("Excluir este registro de tentativa de NF-e?")) return;
    try {
      await api.delete(`/nfe/notes/${n.id}`);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const hasActive = (notes || []).some((n) => ["autorizado", "erro_cancelamento", "processando_autorizacao", "enviando", "desconhecido"].includes(n.status));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="nfe-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-4xl max-h-[94dvh] sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display flex flex-wrap items-center gap-2">
            <Barcode size={22} className="text-indigo-400" /> NF-e do Pedido #{order.code}
            <span className={`text-[11px] border rounded-full px-2.5 py-0.5 ${meta.ambiente === "producao" ? "bg-rose-500/10 text-rose-300 border-rose-500/30" : "bg-sky-500/10 text-sky-300 border-sky-500/30"}`}>
              {meta.ambiente === "producao" ? "PRODUÇÃO" : "HOMOLOGAÇÃO"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {order.client_name} · {fmtBRL(order.total_value)}
          </DialogDescription>
        </DialogHeader>

        {!form && <p className="text-sm text-zinc-500 py-6">Carregando...</p>}

        {form && !meta.emitente_ok && (
          <p data-testid="nfe-config-warning" className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded px-4 py-3">
            Os dados do emitente ainda não foram configurados. Peça ao administrador para preencher em <b>Config. NF-e</b> no menu. A prévia e a emissão dependem disso.
          </p>
        )}
        {form && meta.emitente_ok && !meta.settings_ok && (
          <p data-testid="nfe-token-warning" className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded px-4 py-3">
            Token da Focus NFe ({meta.ambiente}) não configurado — a <b>prévia do DANFE funciona normalmente</b>, mas a emissão ficará disponível após o administrador colar o token em Config. NF-e.
          </p>
        )}

        {form && view === "list" && (
          <div className="space-y-3">
            {(notes || []).length === 0 && <p className="text-sm text-zinc-500">Nenhuma NF-e para este pedido ainda.</p>}
            {(notes || []).map((n) => (
              <div key={n.id} data-testid={`nfe-note-${n.ref}`} className="border border-zinc-800 rounded-lg p-3.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                  <Chip status={n.status} />
                  {(n.cartas_correcao || []).length > 0 && (
                    <span className="text-[11px] font-medium border rounded-full px-2 py-0.5 bg-sky-500/15 text-sky-300 border-sky-500/30">
                      {(n.cartas_correcao || []).length} CC-e
                    </span>
                  )}
                  <span className="text-zinc-300 font-medium">{n.numero ? `NF-e nº ${n.numero} · série ${n.serie}` : `ref ${n.ref}`}</span>
                  {n.protocolo && <span className="text-xs text-zinc-500">protocolo {n.protocolo}</span>}
                  <span className="text-xs text-zinc-600">{new Date(n.created_at).toLocaleString("pt-BR")}</span>
                </div>
                {n.mensagem_sefaz && <p className="text-xs text-zinc-500 mt-1.5">{n.mensagem_sefaz}</p>}
                {n.cancel_mensagem_sefaz && n.status !== "cancelado" && (
                  <div data-testid={`nfe-cancel-recusado-${n.ref}`} className="mt-1.5 border border-rose-500/40 bg-rose-500/10 rounded-md px-2.5 py-2 space-y-1">
                    <p className="text-xs font-bold text-rose-300">Cancelamento recusado pela SEFAZ</p>
                    <p className="text-xs text-rose-200 font-mono break-words">{n.cancel_status_sefaz ? `SEFAZ ${n.cancel_status_sefaz}: ` : ""}{n.cancel_mensagem_sefaz}</p>
                    {n.cancel_rejeicao_amigavel && (
                      <p className="text-xs text-zinc-300"><b>{n.cancel_rejeicao_amigavel.titulo}.</b> {n.cancel_rejeicao_amigavel.explicacao} <span className="text-emerald-300"><b>O que fazer:</b> {n.cancel_rejeicao_amigavel.correcao}</span></p>
                    )}
                  </div>
                )}
                {Array.isArray(n.erros) && n.erros.length > 0 && n.status !== "autorizado" && n.status !== "erro_cancelamento" && (
                  <ul className="text-xs text-rose-400 mt-1.5 list-disc ml-4">
                    {n.erros.map((e, i) => <li key={`${e.codigo || ""}-${(e.mensagem || "").slice(0, 40)}-${i}`}>{e.mensagem || JSON.stringify(e)}</li>)}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {["enviando", "processando_autorizacao", "desconhecido", "erro_cancelamento"].includes(n.status) && (
                    <button data-testid={`nfe-refresh-${n.ref}`} onClick={() => refresh(n)} disabled={busy}
                      className="flex items-center gap-1.5 border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-xs font-medium px-3 py-2 rounded transition-colors">
                      <ArrowsClockwise size={14} /> Atualizar Status
                    </button>
                  )}
                  {(isAutorizada(n.status) || n.status === "cancelado") && (
                    <button data-testid={`nfe-danfe-${n.ref}`} onClick={() => danfe(n)} disabled={busy}
                      className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium px-3 py-2 rounded transition-colors">
                      <FilePdf size={14} /> DANFE (modelo próprio)
                    </button>
                  )}
                  {isAutorizada(n.status) && (
                    <button data-testid={`nfe-xml-${n.ref}`} onClick={() => xml(n)} disabled={busy}
                      className="flex items-center gap-1.5 border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-xs font-medium px-3 py-2 rounded transition-colors">
                      <FileCode size={14} /> XML
                    </button>
                  )}
                  {(n.cartas_correcao || []).length > 0 && (
                    <button data-testid={`nfe-cce-pdf-${n.ref}`} onClick={() => ccePdf(n)} disabled={busy}
                      title={`Baixar o DACCE (PDF) da CC-e nº ${latestCceSeq(n)} — a mais recente, vigente na SEFAZ`}
                      className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-medium px-3 py-2 rounded transition-colors">
                      <FilePdf size={14} /> Baixar PDF da CC-e
                    </button>
                  )}
                  {isAutorizada(n.status) && (
                    <button data-testid={`nfe-cce-${n.ref}`} onClick={() => setCceNote(n)} disabled={busy}
                      className="flex items-center gap-1.5 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-medium px-3 py-2 rounded transition-colors">
                      <NotePencil size={14} /> Carta de Correção
                    </button>
                  )}
                  {isAutorizada(n.status) && isAdmin && (
                    <button data-testid={`nfe-cancel-${n.ref}`} onClick={() => cancel(n)} disabled={busy}
                      className="flex items-center gap-1.5 text-rose-400 hover:bg-rose-500/10 text-xs font-medium px-3 py-2 rounded transition-colors">
                      <XCircle size={14} /> {n.cancel_mensagem_sefaz ? "Tentar cancelar novamente" : "Cancelar NF-e"}
                    </button>
                  )}
                  {!["autorizado", "erro_cancelamento", "cancelado", "processando_autorizacao"].includes(n.status) && (
                    <button data-testid={`nfe-delete-${n.ref}`} onClick={() => removeNote(n)}
                      className="flex items-center gap-1.5 text-zinc-500 hover:text-rose-400 text-xs font-medium px-3 py-2 rounded transition-colors">
                      <Trash size={14} /> Excluir registro
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 pt-1">
              {!hasActive && (
                <button data-testid="nfe-new-button" onClick={() => setView("form")}
                  className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2.5 rounded transition-colors">
                  <Plus size={16} /> Nova NF-e
                </button>
              )}
              <button data-testid="nfe-close-button" onClick={onClose}
                className="border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-sm font-medium px-4 py-2.5 rounded transition-colors">
                Fechar
              </button>
            </div>
          </div>
        )}

        {form && view === "form" && (
          <>
            <NfeForm form={form} setForm={setForm} ultimoNumero={meta.ultimo_numero || 0} />
            <div className="flex flex-wrap gap-2 mt-6 sticky bottom-0 bg-zinc-950 py-3 border-t border-zinc-800">
              {!online && (
                <p data-testid="nfe-offline-banner" className="w-full flex items-center gap-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2.5">
                  <WifiSlash size={15} className="shrink-0" /> Sem internet — a emissão de NF-e está bloqueada até a conexão voltar. (A criação de pedidos continua funcionando offline.)
                </p>
              )}
              <button data-testid="nfe-preview-button" onClick={preview} disabled={busy || !online}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 min-h-[44px] rounded transition-colors">
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <Eye size={16} />} Prévia do DANFE
              </button>
              <button data-testid="nfe-emit-button" onClick={emit} disabled={busy || !meta.settings_ok || !online}
                title={!online ? "Sem conexão com a internet" : !meta.settings_ok ? "Configure o token da Focus NFe primeiro" : ""}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 min-h-[44px] rounded transition-colors">
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />} Emitir NF-e
              </button>
              {(notes || []).length > 0 && (
                <button onClick={() => setView("list")} data-testid="nfe-back-list"
                  className="border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-sm font-medium px-4 py-2.5 min-h-[44px] rounded transition-colors">
                  Ver notas emitidas
                </button>
              )}
              <button onClick={onClose} data-testid="nfe-form-close-button"
                className="border border-zinc-700 hover:bg-zinc-800 text-zinc-400 text-sm font-medium px-4 py-2.5 min-h-[44px] rounded transition-colors ml-auto">
                Fechar
              </button>
              <p className="w-full text-[11px] text-zinc-500 -mt-0.5">A emissão da NF-e exige conexão com a internet no momento do envio.</p>
            </div>
          </>
        )}
        {confirmOpen && form && (
          <NfeConfirmDialog form={form} ambiente={meta.ambiente} origem={`Pedido #${order.code}`} busy={busy}
            onConfirm={doEmit} onClose={() => setConfirmOpen(false)} />
        )}
        {cceNote && <CceDialog note={cceNote} onClose={() => setCceNote(null)} onDone={() => load()} />}
      </DialogContent>
    </Dialog>
  );
};
